import {SelectOptionItem} from '@logistics-frontend/blocks'
import {Translator} from '@logistics-frontend/core/i18n'
import {roundNumber} from '@logistics-frontend/utils/money'
import {get} from 'lodash'

import {Category, IntervalType, SpecialTaximeter, TariffInterval, ZonalPrice} from './types'

type Price = SpecialTaximeter['price']

const WAITING_PRICE_TYPE = {
  fixed: 'fixed',
  perMinute: 'per_minute',
}

// Selectors
const getSpecialTaximiterForZone = (tariff: Category, zone: string) =>
  (tariff?.special_taximeters ?? []).find(({zone_name}) => zone_name === zone)

export const getFieldFromTaximiterPrice =
  (field: Exclude<keyof Price, 'time_price_intervals_meter_id' | 'distance_price_intervals_meter_id'>) =>
  (tariff: Category, zone: string) => {
    const zoneItem = getSpecialTaximiterForZone(tariff, zone)
    return zoneItem?.price?.[field] ?? []
  }
export const getMinFromIntervals = (intervals: TariffInterval[]) => {
  if (!intervals.length) {
    return null
  }
  const intervalBegins = intervals.map(interval => interval?.begin || 0)
  return Math.min(...intervalBegins) || null
}

type WhiteListItem = {
  unit?: string
  value?: number | null | string
  label?: string
}

export const formatWhiteListItemsProp = (items: WhiteListItem[]) =>
  items.reduce<SelectOptionItem[]>(
    (acc, {unit, value, label, subLabel}) =>
      value || value === 0 ? acc.concat({label, value: `${roundNumber(+value)} ${unit}`, subLabel}) : acc,
    [],
  )

type IntervalTranslations = {
  onlyBegin: string
  withEnd: string
  fromZero: string
}

const mapIntervalToListItem =
  ({onlyBegin, withEnd, fromZero}: IntervalTranslations, t: Translator) =>
  ({begin, end, price}: TariffInterval, unit: string) => {
    const label =
      // NOTE: Если у интервала нет конца и он начинается с 0 берем специальный шаблон
      !end && begin === 0
        ? t(fromZero)
        : !end // NOTE: Применяем соответсвующий шаблон для интервала с концом и без
        ? t(onlyBegin, {placeholder: {begin}})
        : t(withEnd, {placeholder: {begin, end}})

    return {
      value: price,
      label,
      unit,
    }
  }
const formatTimeIntervals = (t: Translator) =>
  mapIntervalToListItem(
    {
      withEnd: t('tariffs.property.time.with_end'),
      onlyBegin: t('tariffs.property.time.only_begin'),
      fromZero: t('tariffs.property.time'),
    },
    t,
  )
const formatDistanceIntervals = (t: Translator) =>
  mapIntervalToListItem(
    {
      withEnd: t('tariffs.property.distance.with_end'),
      onlyBegin: t('tariffs.property.distance.only_begin'),
      fromZero: t('tariffs.property.distance'),
    },
    t,
  )

// List Items getters
export const getPaidWaiting = (
  {waiting_price_type, waiting_price}: Pick<Category, 'waiting_price_type' | 'waiting_price'>,
  currency: string,
  t: Translator,
) => {
  const listItem = {
    label: t('tariffs.property.paid_waiting'),
    value: waiting_price,
  }
  const isPerMinute = !waiting_price_type && waiting_price !== undefined

  if (waiting_price_type === WAITING_PRICE_TYPE.fixed) {
    return {...listItem, unit: currency}
  }

  if (waiting_price_type === WAITING_PRICE_TYPE.perMinute || isPerMinute) {
    return {...listItem, unit: `${currency}/${t('tariffs.units.time')}`}
  }

  return {value: null}
}

const getIntervalConfig = (t: Translator) => ({
  time: {
    field: 'time_price_intervals',
    formater: formatTimeIntervals(t),
  },
  distance: {
    field: 'distance_price_intervals',
    formater: formatDistanceIntervals(t),
  },
})

const getTaximiterInterval =
  (intervalType: IntervalType) => (tariff: Category, zone: string, unit: string, t: Translator) => {
    const field = `${intervalType}_price_intervals` as Exclude<
      keyof Price,
      'time_price_intervals_meter_id' | 'distance_price_intervals_meter_id'
    >
    const {formater} = getIntervalConfig(t)[intervalType]
    const intervals = getFieldFromTaximiterPrice(field)(tariff, zone)
    return intervals.map(interval => formater(interval, unit))
  }

export const getFromTransferDetails = (path: string[], def?: unknown) => (transferDetails: ZonalPrice) =>
  get(transferDetails, ['price', ...path], def || null)

const getTransferInterval =
  (intervalType: IntervalType) => (transferDetails: ZonalPrice, unit: string, t: Translator) => {
    const {field, formater} = getIntervalConfig(t)[intervalType]
    const intervals = getFromTransferDetails([field], [])(transferDetails)
    return intervals.map((interval: TariffInterval) => formater(interval, unit))
  }

export const getTaximiterDistanceIntervals = getTaximiterInterval('distance')
export const getTaximiterTimeIntervals = getTaximiterInterval('time')

export const getTransferDistanceIntervals = getTransferInterval('distance')
export const getTransferTimeIntervals = getTransferInterval('time')

// Sorters & Filters
export const sortArrayBy =
  <T extends unknown>(path: string) =>
  (array: T[]) =>
    array.sort((item1, item2) => (get(item1, path) > get(item2, path) ? 1 : -1))

export const filterTariffsByFields = (tariffs: Category[], fields: Partial<Category>) =>
  tariffs.filter(item => Object.entries(fields).every(([key, value]) => item[key] === value))

// Translations
export const getDayTypes = (t: Translator) => [
  t('tariffs.filter.day.weekday'),
  t('tariffs.filter.day.weekend'),
  t('tariffs.filter.day.everyday'),
]

export const getCategoryTypes = (t: Translator) => ({
  application: t('tariffs.filter.category_type.application'),
  call_center: t('tariffs.filter.category_type.call_center'),
})
